import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { handleSubmitError } from '@app/forms/form.helper';
import { ValidationService } from '@app/forms/validation.service';
import { useVatValidator } from '@app/shared/async-validators/vat-validator/vat.validator';
import { VatResponseStatus } from '@app/shared/billing/billing.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccountService } from '@reducers/account/account.service';
import { AuthService } from '@reducers/auth/auth.service';
import { ButtonComponent } from '@sb/ui/button';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { FormGroupComponent, FormGroupPrefixDirective, FormGroupSuffixDirective } from '@sb/ui/form-group';
import { IconComponent } from '@sb/ui/icon';
import { InputDirective } from '@sb/ui/input';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import { SpinnerComponent } from '@sb/ui/spinner';
import { BillingService } from '@shared/billing/billing.service';
import { ContentStateComponent } from '@shared/content-state/content-state.component';
import { SharedLaddaModule } from '@shared/shared-ladda.module';
import { of, Subscription } from 'rxjs';

export interface VatChanges {
  status: VatResponseStatus;
  vatNumber: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'billing-details-form',
  templateUrl: './billing-details-form.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    SharedLaddaModule,
    ContentStateComponent,
    IconComponent,
    CommonModule,
    SpinnerComponent,
    SbFormFieldComponent,
    ButtonComponent,
    SelectComponent,
    InputDirective,
    FormGroupPrefixDirective,
    FormGroupSuffixDirective,
    FormGroupComponent,
  ],
  styles: [':host { display: block; }'],
})
export class BillingDetailsFormComponent implements OnInit, OnDestroy {
  private accountService = inject(AccountService);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private billingService = inject(BillingService);

  @Input()
  backButtonEnabled: boolean;
  @Input()
  saveLabel: string;
  @Input()
  hideButtonBar = false;
  @Input()
  noPadding = false;
  @Output()
  backPressed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  saved: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  countryCodeChange = new EventEmitter<string>();
  @Output()
  vatStatusChange = new EventEmitter<VatChanges>();

  isLoading: boolean;
  isDataLoading = true;

  error: string;
  countries: SelectItem[] = [];

  vatResponseStatus = VatResponseStatus;

  @Input()
  hideSubmitButton = false;

  private dataSubs = new Subscription();

  billingDetailsForm = new FormGroup({
    invoice_company: new FormControl('', [Validators.required]),
    invoice_email: new FormControl('', [Validators.required, ValidationService.emailValidator]),
    first_name: new FormControl('', [Validators.required]),
    last_name: new FormControl('', [Validators.required]),
    street_address: new FormControl('', [Validators.required]),
    zipcode: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    vat: new FormControl(''),
  });

  vatValidator = useVatValidator(this.billingDetailsForm.controls.country);

  ngOnInit(): void {
    this.billingDetailsForm.controls.vat.addAsyncValidators([this.vatValidator.validate]);

    this.dataSubs.add(
      this.accountService.fetchInvoiceSettings().subscribe({
        next: (res) => {
          const account = res.Account;
          this.billingDetailsForm.patchValue(account);
          this.isDataLoading = false;
        },
      }),
    );

    this.dataSubs.add(
      this.authService.fetchCountries(this.translateService.getCurrentLang() ?? DefaultLocale).subscribe({
        next: (countries) => {
          this.countries = Object.entries(countries).map(([value, label]) => ({ text: label, value }));
        },
      }),
    );

    this.dataSubs.add(
      this.billingDetailsForm.controls.country.valueChanges.subscribe((countryCode) => {
        this.billingDetailsForm.get('vat').updateValueAndValidity();
        this.countryCodeChange.emit(countryCode);
      }),
    );

    this.dataSubs.add(
      this.billingDetailsForm.get('vat').statusChanges.subscribe((status) => {
        if (status !== 'PENDING') {
          // Wait until the async validator has finished
          this.vatStatusChange.emit({
            status: this.vatValidator.status(),
            vatNumber: this.billingDetailsForm.get('vat').value,
          });
        }
      }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  submit() {
    if (!this.billingDetailsForm.valid || this.isLoading) {
      return of(false);
    }

    this.isLoading = true;

    const form = this.billingDetailsForm.value;
    const request = this.accountService.patchInvoiceSettings(form);

    this.dataSubs.add(
      request.subscribe({
        next: () => {
          this.billingService.billingUpdate$.next(null);
          this.isLoading = false;
          this.saved.emit();
        },
        error: (err) => this.handleError(err),
      }),
    );

    return request;
  }

  handleError(error: HttpErrorResponse) {
    this.isLoading = false;
    handleSubmitError(error, this.billingDetailsForm);
  }
}
